import React from 'react';
import { User, ExternalLink, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Format message content - converts markdown-like syntax to styled JSX
 */
const formatMessageContent = (text) => {
  if (!text) return null;

  // Split by lines to handle different formatting
  const lines = text.split('\n');
  const elements = [];
  let currentParagraph = [];
  let listItems = [];
  let listKey = 0;

  lines.forEach((line, index) => {
    const trimmedLine = line.trim();

    // Handle headings (# Title or ## Title)
    if (trimmedLine.startsWith('#')) {
      // Close current paragraph if exists
      if (currentParagraph.length > 0) {
        elements.push(
          <p key={`p-${index}`} className="mb-2 last:mb-0">
            {formatInlineText(currentParagraph.join(' '))}
          </p>
        );
        currentParagraph = [];
      }
      
      // Close list if exists
      if (listItems.length > 0) {
        elements.push(
          <ul key={`ul-${listKey++}`} className="list-disc list-inside mb-2 space-y-1 ml-2">
            {listItems.map((item, idx) => (
              <li key={idx} className="text-sm">
                {formatInlineText(item)}
              </li>
            ))}
          </ul>
        );
        listItems = [];
      }

      const level = trimmedLine.match(/^#+/)[0].length;
      const titleText = trimmedLine.replace(/^#+\s*/, '').trim();
      
      if (level === 1) {
        elements.push(
          <h3 key={`h1-${index}`} className="text-base font-bold text-gray-900 mb-2 mt-3 first:mt-0">
            {formatInlineText(titleText)}
          </h3>
        );
      } else if (level === 2) {
        elements.push(
          <h4 key={`h2-${index}`} className="text-sm font-semibold text-gray-800 mb-1.5 mt-2 first:mt-0">
            {formatInlineText(titleText)}
          </h4>
        );
      } else {
        elements.push(
          <h5 key={`h3-${index}`} className="text-sm font-medium text-gray-700 mb-1 mt-2 first:mt-0">
            {formatInlineText(titleText)}
          </h5>
        );
      }
      return;
    }

    // Handle list items (- or *)
    if (trimmedLine.match(/^[-*]\s+/)) {
      // Close current paragraph if exists
      if (currentParagraph.length > 0) {
        elements.push(
          <p key={`p-${index}`} className="mb-2">
            {formatInlineText(currentParagraph.join(' '))}
          </p>
        );
        currentParagraph = [];
      }
      
      const listText = trimmedLine.replace(/^[-*]\s+/, '').trim();
      listItems.push(listText);
      return;
    }

    // Handle empty lines
    if (trimmedLine === '') {
      // Close list if exists
      if (listItems.length > 0) {
        elements.push(
          <ul key={`ul-${listKey++}`} className="list-disc list-inside mb-2 space-y-1 ml-2">
            {listItems.map((item, idx) => (
              <li key={idx} className="text-sm">
                {formatInlineText(item)}
              </li>
            ))}
          </ul>
        );
        listItems = [];
      }
      
      if (currentParagraph.length > 0) {
        elements.push(
          <p key={`p-${index}`} className="mb-2 last:mb-0">
            {formatInlineText(currentParagraph.join(' '))}
          </p>
        );
        currentParagraph = [];
      }
      return;
    }

    // Regular text line
    if (listItems.length > 0) {
      elements.push(
        <ul key={`ul-${listKey++}`} className="list-disc list-inside mb-2 space-y-1 ml-2">
          {listItems.map((item, idx) => (
            <li key={idx} className="text-sm">
              {formatInlineText(item)}
            </li>
          ))}
        </ul>
      );
      listItems = [];
    }
    currentParagraph.push(trimmedLine);
  });

  // Close any remaining elements
  if (listItems.length > 0) {
    elements.push(
      <ul key={`ul-${listKey++}`} className="list-disc list-inside mb-2 space-y-1 ml-2">
        {listItems.map((item, idx) => (
          <li key={idx} className="text-sm">
            {formatInlineText(item)}
          </li>
        ))}
      </ul>
    );
  }
  if (currentParagraph.length > 0) {
    elements.push(
      <p key="p-final" className="mb-2 last:mb-0">
        {formatInlineText(currentParagraph.join(' '))}
      </p>
    );
  }

  return elements.length > 0 ? elements : formatInlineText(text);
};

/**
 * Format inline text with bold, italic, etc.
 */
const formatInlineText = (text) => {
  if (!text) return null;

  const parts = [];
  let lastIndex = 0;
  let key = 0;

  // Match **bold**, *italic*, and combinations
  const regex = /(\*\*[^*]+\*\*|\*[^*]+\*|__[^_]+__|_[^_]+_)/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    const matchedText = match[0];
    let formatted;

    // **bold** or __bold__
    if (matchedText.startsWith('**') || matchedText.startsWith('__')) {
      const content = matchedText.replace(/^\*\*|__|\*\*$|__$/g, '');
      formatted = <strong key={key++} className="font-semibold text-gray-900">{content}</strong>;
    }
    // *italic* or _italic_
    else if (matchedText.startsWith('*') || matchedText.startsWith('_')) {
      const content = matchedText.replace(/^[*_]|[*_]$/g, '');
      formatted = <em key={key++} className="italic text-gray-700">{content}</em>;
    }

    parts.push(formatted);
    lastIndex = match.index + matchedText.length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : text;
};

/**
 * ChatMessage Component
 * Renders a single message in the chat interface
 */
const ChatMessage = ({ 
  message, 
  isUser = false, 
  isLoading = false,
  variant = 'default', // 'default' | 'search' | 'assistant'
  onActionClick,
}) => {
  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-start">
        <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3 shadow-sm">
          <div className="flex items-center space-x-2">
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            <span className="text-sm text-gray-500">Réflexion en cours...</span>
          </div>
        </div>
      </div>
    );
  }

  const { content, data, actions } = typeof message === 'string' 
    ? { content: message, data: null, actions: null }
    : message;

  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-4 py-3 shadow-sm',
          isUser
            ? 'bg-gradient-to-br from-[#FF9F68] to-[#E67E4F] text-white'
            : 'bg-white border border-gray-100 text-gray-800'
        )}
      >
        {/* Avatar for assistant messages */}
        {!isUser && (
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-gray-500">Cynthia</span>
          </div>
        )}

        {/* Message content */}
        <div className={cn(
          'text-sm leading-relaxed',
          isUser ? 'text-white' : 'text-gray-700'
        )}>
          {isUser ? (
            <p className="whitespace-pre-wrap">{content}</p>
          ) : (
            <div className="space-y-1">
              {formatMessageContent(content)}
            </div>
          )}
        </div>

        {/* Data display for assistant responses */}
        {data && !isUser && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <DataDisplay data={data} />
          </div>
        )}

        {/* Quick actions */}
        {actions && actions.length > 0 && !isUser && (
          <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-2">
            {actions.map((action, index) => (
              <button
                key={index}
                onClick={() => onActionClick?.(action)}
                className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-orange-50 text-orange-700 hover:bg-orange-100 transition-colors"
              >
                {action.label}
                {action.action === 'navigate' && <ExternalLink className="h-3 w-3" />}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * DataDisplay Component
 * Renders structured data from AI responses
 */
const DataDisplay = ({ data }) => {
  if (!data || (Array.isArray(data) && data.length === 0)) return null;

  // Handle array of tool results
  if (Array.isArray(data)) {
    return (
      <div className="space-y-2">
        {data.map((item, index) => (
          <ToolResultDisplay key={index} toolResult={item} />
        ))}
      </div>
    );
  }

  // Handle single object
  return <ObjectDisplay data={data} />;
};

/**
 * ToolResultDisplay Component
 * Renders a single tool result
 */
const ToolResultDisplay = ({ toolResult }) => {
  const { tool, result } = toolResult;

  if (result?.error) {
    return (
      <div className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">
        Erreur: {result.message}
      </div>
    );
  }

  // Different display based on tool type
  switch (tool) {
    case 'get_dashboard_stats':
      return <StatsDisplay stats={result} />;
    
    case 'get_my_leads':
    case 'get_contact_requests':
      return <LeadsListDisplay items={result?.items || []} total={result?.total} />;
    
    case 'get_my_visits':
      return <VisitsListDisplay visits={result} />;
    
    case 'get_my_favorites':
    case 'search_properties':
      return <PropertiesListDisplay items={result?.items || result || []} />;
    
    default:
      return <ObjectDisplay data={result} compact />;
  }
};

/**
 * StatsDisplay Component
 */
const StatsDisplay = ({ stats }) => {
  if (!stats) return null;

  const statItems = Object.entries(stats)
    .filter(([key, value]) => typeof value === 'number' || typeof value === 'string')
    .slice(0, 6);

  return (
    <div className="grid grid-cols-2 gap-2">
      {statItems.map(([key, value]) => (
        <div key={key} className="bg-gray-50 rounded-lg px-3 py-2">
          <div className="text-lg font-semibold text-gray-900">{value}</div>
          <div className="text-xs text-gray-500 capitalize">
            {key.replace(/([A-Z])/g, ' $1').trim()}
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * LeadsListDisplay Component
 */
const LeadsListDisplay = ({ items, total }) => {
  if (!items || items.length === 0) {
    return <div className="text-xs text-gray-500">Aucun lead trouvé</div>;
  }

  return (
    <div className="space-y-2">
      <div className="text-xs text-gray-500 mb-2">
        {total ? `${total} lead(s) trouvé(s)` : `${items.length} lead(s)`}
      </div>
      {items.slice(0, 5).map((lead, index) => (
        <div key={lead.id || index} className="bg-gray-50 rounded-lg px-3 py-2">
          <div className="font-medium text-sm text-gray-900">{lead.seekerName}</div>
          <div className="text-xs text-gray-500">
            {lead.property?.title || 'Bien non spécifié'} • {lead.status}
          </div>
        </div>
      ))}
      {items.length > 5 && (
        <div className="text-xs text-orange-600">+ {items.length - 5} autres...</div>
      )}
    </div>
  );
};

/**
 * VisitsListDisplay Component
 */
const VisitsListDisplay = ({ visits }) => {
  if (!visits || visits.length === 0) {
    return <div className="text-xs text-gray-500">Aucune visite planifiée</div>;
  }

  return (
    <div className="space-y-2">
      {visits.slice(0, 5).map((visit, index) => (
        <div key={visit.id || index} className="bg-gray-50 rounded-lg px-3 py-2">
          <div className="font-medium text-sm text-gray-900">
            {new Date(visit.visitDate).toLocaleDateString('fr-FR', {
              weekday: 'short',
              day: 'numeric',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
          <div className="text-xs text-gray-500">
            {visit.seekerName || visit.property?.title}
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * PropertiesListDisplay Component
 */
const PropertiesListDisplay = ({ items }) => {
  if (!items || items.length === 0) {
    return <div className="text-xs text-gray-500">Aucun bien trouvé</div>;
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'decimal',
      maximumFractionDigits: 0,
    }).format(price) + ' FCFA';
  };

  return (
    <div className="space-y-2">
      {items.slice(0, 4).map((property, index) => (
        <div key={property.id || index} className="bg-gray-50 rounded-lg px-3 py-2">
          <div className="font-medium text-sm text-gray-900 line-clamp-1">
            {property.title}
          </div>
          <div className="text-xs text-gray-500 flex items-center justify-between">
            <span>{property.city}</span>
            <span className="font-medium text-orange-600">
              {formatPrice(property.price)}
            </span>
          </div>
        </div>
      ))}
      {items.length > 4 && (
        <div className="text-xs text-orange-600">+ {items.length - 4} autres biens...</div>
      )}
    </div>
  );
};

/**
 * ObjectDisplay Component
 * Generic object display
 */
const ObjectDisplay = ({ data, compact = false }) => {
  if (!data || typeof data !== 'object') return null;

  const entries = Object.entries(data)
    .filter(([key, value]) => 
      value !== null && 
      value !== undefined && 
      !key.startsWith('_') &&
      typeof value !== 'function'
    )
    .slice(0, compact ? 4 : 8);

  if (entries.length === 0) return null;

  return (
    <div className={cn(
      'text-xs space-y-1',
      compact ? 'bg-gray-50 rounded-lg p-2' : ''
    )}>
      {entries.map(([key, value]) => (
        <div key={key} className="flex justify-between">
          <span className="text-gray-500 capitalize">
            {key.replace(/([A-Z])/g, ' $1').trim()}:
          </span>
          <span className="font-medium text-gray-700 ml-2">
            {typeof value === 'object' ? JSON.stringify(value).slice(0, 30) : String(value)}
          </span>
        </div>
      ))}
    </div>
  );
};

export default ChatMessage;

